import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import queryString from 'query-string';

import { Table, TableBody, TableCell, TableContainer, TableRow, Button } from '@mui/material';
import Chip from '@mui/material/Chip';

import { useEnumApi } from 'contexts/EnumApiContext';
import MainCard from 'components/MainCard';
import CompanyEquipmentViewer from 'components/viewer/CompanyEquipmentViewer';
import CompanySvgViewer from 'components/viewer/CompanySvgViewer';
import AlertListCard from 'components/inner/mainCard/AlertListCard';

import { equipmentISOStatus, getEquipmentCtgrImageNameByCodeOfDashboardSub, getEquipmentStatusColorByStatus, getTemperatureColor, ROLE, uniqueByEquipmentPartIdAndLogCode } from 'data/data';
import { getEquipmentCtgr1ByZone, getEquipmentMeasurementLogsLatestQuery, getEquipmentsByQuery } from 'components/axios/equipmentAxios';
import { getEquipmentAlertsByQueryString, getLatestTemperatureByEquipmentsByQuery } from 'components/axios/equipmentAlertsAxios';
import { getEquipmentZonesById } from 'components/axios/equipmentZoneAxios';
import { getCompanyById } from 'components/axios/companyAxios';
import DashboardDurationGraphs from 'components/inner/chart/DashboardDurationGraphs';
import { useQuery } from '@tanstack/react-query';
import CompanyImageViewer from 'components/viewer/CompanyImageViewer';

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;
const IAQ_DELAY = 1 * 60 * 1000; // 1분

function DashboardMoniteringSub() {
  const isViewCommError = process.env.REACT_APP_SHOW_NETWORK_ERROR_TO_ALL_YN === 'Y';
  const { getEnumValueByEnumNameAndCode, role } = useEnumApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const COMM_STATUS_LIST = [
    { id: 'N', value: false,  name: t('message.common.normal') },
    { id: 'Y', value: true,   name: t('message.common.subtitle.communication.issue') },
  ];

  const COMM_STATUS_MAP = COMM_STATUS_LIST.reduce((acc, item) => {
    acc[item.value] = item.name;
    return acc;
  }, {});

  let qs = queryString.parse(window.location.search);

  const { data: baseData } = useQuery({
    queryKey: ['dashboard-sub-base', qs.zId, qs.mId],

    enabled: !!qs.zId,

    staleTime: 0,

    queryFn: async () => {
      const [
        zoneInfo,
        // equipments,
        equipmentCtgr1
      ] = await Promise.all([
        getEquipmentZonesById(qs.zId),

        // getEquipmentsByQuery({
        //   "zone.id": qs.zId,
        //   iotInstlYn: true
        // }),

        getEquipmentCtgr1ByZone({
          "zone.id": qs.zId
        })
      ]);

      let zoneLocationMap = null;

      if (zoneInfo?.locationMaps?.length > 0) {
        if (qs.mId) {
          zoneLocationMap =
            zoneInfo.locationMaps.find(
              m => m.id == qs.mId
            );
        } else {
          zoneLocationMap =
            zoneInfo.locationMaps[0];
        }
      }

      let company = null;

      if (zoneInfo?.managementCompany?.id) {
        company = await getCompanyById(
          zoneInfo.managementCompany.id
        );
      }

      return {
        zoneInfo,
        zoneLocationMap,
        company,
        // equipments:
        //   equipments?._embedded?.equipments ?? [],
        equipmentCategoryLevel1s:
          equipmentCtgr1 ?? []
      };
    }
  });

  const { data: liveData } = useQuery({
    queryKey: ['dashboard-sub-live', qs.zId],

    enabled: !!qs.zId,

    refetchInterval: DELAY_TIME,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,

    placeholderData: previousData => previousData,

    staleTime: DELAY_TIME,

    queryFn: async () => {

      let alertParam = {
        page: 0,
        size: 12,
        // "equipment.iotInstlYn": true,
        "equipment.opStatus": "O",
        "equipment.zone.id": qs.zId,
      };

      let addQueryString =
        "&sort=status,asc&sort=alertedAt,desc";

      let queryString = Object.entries(alertParam)
        .map(([key, value]) =>
          value && key + '=' + value
        )
        .filter(v => v)
        .join('&');

      queryString += addQueryString;

      const [
        temps,
        alerts,
        equipments
      ] = await Promise.all([
        getLatestTemperatureByEquipmentsByQuery({}),

        getEquipmentAlertsByQueryString(
          "?" + queryString
        ),

        getEquipmentsByQuery({
          "zone.id": qs.zId,
          size: 1000,
          // iotInstlYn: true,
          opStatus : "O"
        })
      ]);

      return {
        equipmentLatestTemperatures: temps,

        alertDatas:
          alerts?._embedded?.equipmentAlerts ?? [],

        equipments:
          equipments?._embedded?.equipments ?? []
      };
    }
  });

  const { data: iaqData } = useQuery({
    queryKey: ['dashboard-sub-iaq', qs.zId],

    enabled: !!qs.zId,

    refetchInterval: IAQ_DELAY,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    placeholderData: previousData => previousData,

    staleTime: IAQ_DELAY,

    queryFn: async () => {

      const data =
        await getEquipmentMeasurementLogsLatestQuery({
          "equipment.zone.id": qs.zId,
          "equipmentCtgr.code": "controlUnit",
          "equipmentPart.masterEquipmentPart.code":
            "IAQSensor"
        });

      const filtered = data.filter(
        (m) =>
          m.componentCode === 'controlUnit' &&
          m.equipmentPartCode === 'IAQSensor' &&
          m.componentLocationCode === 'RA' &&
          (
            m.logCode === 'T' ||
            m.logCode === 'RH' ||
            m.logCode === 'CO2'
          )
      );

      return {
        latestLogs: uniqueByEquipmentPartIdAndLogCode(filtered)
      }
    }
  });

  const equipments =
    liveData?.equipments
    // ?? baseData?.equipments
    ?? [];
  
  const equipmentCategoryLevel1s  = baseData?.equipmentCategoryLevel1s ?? [];
  const zoneInfo                  = baseData?.zoneInfo ?? null;
  const zoneLocationMap           = baseData?.zoneLocationMap ?? null
  const company                   = baseData?.company ?? null;

  const equipmentLatestTemperatures = liveData?.equipmentLatestTemperatures ?? [];
  const alertDatas                  = liveData?.alertDatas ?? [];
  
  const latestLogs                = iaqData?.latestLogs ?? [];

  // useEffect(() => {
  //   console.log('PARENT latestLogs', latestLogs);
  // }, [latestLogs]);
  
  if (!baseData || !liveData) {
    return <div />; // 또는 로딩 스피너
  } else {
  return (
      <div className="main_container cn_sub_container">
        <div className="sub_companyType">
          <span>･</span>{zoneInfo?.managementCompany?.info?.name}
          {company?.equipmentZones?.map((zone, bi)=>{
            return (
              <Button key={bi} 
                variant={zone.id == qs.zId ? "contained":"outlined"} 
                color="primary" 
                style={{margin:'5px'}}
                onClick={()=>{
                  navigate(`/dashboard/sub?zId=${zone.id}`);
                  // window.location.href = `/dashboard/sub?zId=${zone.id}`
                }}
              >{zone.name}</Button>
            )
          })}
        </div>

        {/* company image */}
        <div className="sub_motion_train">
          {(zoneLocationMap /*&& equipments && equipments.length > 0*/) ? 
          <React.Fragment>
            {zoneLocationMap.file?.contentType === "image/svg+xml" ? 
          <CompanySvgViewer fileId={zoneLocationMap?.file?.id} zoneId={zoneInfo?.id} mapsId={zoneLocationMap?.id} 
              zoneLocationMap={zoneInfo?.locationMaps} equipments={equipments} latestLogs={latestLogs} />
            :
          <CompanyEquipmentViewer fileId={zoneLocationMap?.file?.id} zoneId={zoneInfo?.id} mapsId={zoneLocationMap?.id} 
              zoneLocationMap={zoneInfo?.locationMaps} equipments={equipments} latestLogs={latestLogs} />
            }
          </React.Fragment>
          :
          <CompanyImageViewer fileUrl={`${process.env.PUBLIC_URL}/img/bs.png`} mapsId={null} zoneId={zoneInfo?.id} 
              zoneLocationMap={zoneInfo?.locationMaps} equipments={equipments} latestLogs={latestLogs} />
          // <div style={{width:"100%", minHeight:'580px', textAlign:"center"}}>
          //   <img src={`${process.env.PUBLIC_URL}/img/bs.png`} style={{width:"99%", minHeight:'580px', borderRadius:'12px', overflow:'hidden'}} />
          // </div>
          }
        </div>

        <div className='dash_sub_alram' style={{display:"flex", width:"100%"}}>
          <MainCard content={false} className="" style={{padding:"10px", width: "30%"}}>
            <div className='alert_phrase'>
              <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                  <Chip label={t('message.common.chip.dashboard.alert.list')} color="primary" />
                </div>
              </div>

              <div className="charts_box2">
                <AlertListCard equipmentAlerts={alertDatas} target='sd' />
              </div>
            </div>
          </MainCard>

          <MainCard content={false} className="" style={{padding:"10px", width: "70%"}}>
            {zoneInfo && equipments?.length>0 && <DashboardDurationGraphs equipments={equipments} equipmentZoneId={zoneInfo?.id} />}
          </MainCard>
        </div>
        
        {/* category equipment list */}
        {(equipments && equipments.length > 0) && 
        <div className='equip_table' style={{position: "relative", zIndex: "99", top: "317px", paddingBottom: "50px"}}>
          <MainCard  content={false} title="">
          {equipmentCategoryLevel1s && equipmentCategoryLevel1s.map((lvl1, l)=> {
            // console.log(lvl1)
            const equipmentsByLvl1 = (equipments != null ? equipments.filter(equip => equip.equipmentCtgr?.parent?.parent?.code === lvl1.code) : []);
            // console.log(equipmentsByLvl1)
            return (
            <div key={l} style={{marginTop:"20px"}}>
              <div>
                {/* {(selectTab === 'ALL' || selectTab === lvl1.code) &&  */}
                <Chip label={`${lvl1.name} (${equipmentsByLvl1.length})`} className='sub_status' style={{margin:"0 0 10px 10px"}} />
                <div className="small_motion_box">
                  {equipmentsByLvl1 && equipmentsByLvl1.map((equip, e)=>{
                    // console.log(equip)
                    // console.log(equipmentStatuss)
                    // const statusByEquip = equipmentStatuss && equipmentStatuss.find(equ => equ.id === equip.id);
                    // console.log(statusByEquip);
                    const temperatureByEquip = equipmentLatestTemperatures && equipmentLatestTemperatures.find(equ => equ.id === equip.id);
                    // console.log(temperatureByEquip);
                    let imgName = 'noimage.gif';
                    let imgClassName = 'bgwhite';
                    // if (lvl1.code === 'EV') {
                    if (lvl1.code === 'EV' || equip.equipmentCtgr?.code.startsWith('GEARED.') || equip.equipmentCtgr?.code.startsWith('GEARLESS.')) {
                      imgClassName = 'elevator_motion';
                      if (equip.latestEquipmentOp != null) {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr?.code, equip.latestEquipmentOp?.opStatus, equip.latestEquipmentOp?.opStatusDetail, equip.latestEquipmentOp?.doorStatus);
                      } else {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr?.code, equip.latestEquipmentOp?.opStatus);
                      }
                    // } else if (lvl1.code === 'ES') {
                    } else if (lvl1.code === 'ES' || equip.equipmentCtgr?.code.startsWith('DOUBLE.') || equip.equipmentCtgr?.code.startsWith('SINGLE.')) {
                      imgClassName = 'elevator_motion';
                      if (equip.latestEquipmentOp != null) {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr?.code, equip.latestEquipmentOp?.opStatus, equip.latestEquipmentOp?.opStatusDetail);
                      } else {
                        imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr?.code, equip.latestEquipmentOp?.opStatus);
                      }
                    } else {
                      // console.log(equip.latestEquipmentOp)
                      imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr?.code, equip.latestEquipmentOp?.opStatus);
                    }
                    // imgName = getEquipmentCtgrImageNameByCodeOfDashboardSub(equip.equipmentCtgr.code, equip.latestEquipmentOp?.opStatus, equip.latestEquipmentOp?.opStatusDetail, equip.latestEquipmentOp?.doorStatus);

                    const isoGrade = equipmentISOStatus.find(iso=>iso.code === equip.lowestDiagnosisRank);

                    const alertEquipCnt = (equip.status === 'O3_A') ? 1 : 0;
                    const referEquipCnt = (equip.status === 'O2_I') ? 1 : 0;

                    let equipmentOpStatusClassName = '';
                    let equipmentOpStatusText = '';
                    if (equip.hasCommunicationIssue) {
                      equipmentOpStatusClassName = 'font_gray';
                      equipmentOpStatusText = COMM_STATUS_MAP[equip.hasCommunicationIssue];
                    } else {
                      equipmentOpStatusClassName = 'font_green';
                      equipmentOpStatusText = COMM_STATUS_MAP[false];
                    }

                    // let equipmentOpStatusText = '';
                    // if ((isViewCommError || role === ROLE.SA.code) && equip.hasCommunicationIssue) {
                    //     equipmentOpStatusClassName = 'font_gray';
                    //     equipmentOpStatusText = t('message.common.subtitle.communication.issue');
                    // } else {
                    //   if (equip.opStatus === 'S') {
                    //     equipmentOpStatusClassName = 'font_red';
                    //   } else if (equip.opStatus === 'O') {
                    //     equipmentOpStatusClassName = 'font_green';
                    //   }
                    //   equipmentOpStatusText = getEnumValueByEnumNameAndCode("EquipmentOpStatus",equip.opStatus)?.[0]?.title;
                    // }

                    let equipmentOpStatusClassName2 = '';
                    // console.log(equip.opStatus)
                    if (equip.hasCommunicationIssue) {
                    // if (equip.opStatus === 'S') {
                      equipmentOpStatusClassName2 = ' opacity03';
                    }
              // {params.row?.hasCommunicationIssue ? t('message.common.subtitle.communication.issue') : getEnumValueByEnumNameAndCode("EquipmentOpStatus", params.row?.opStatus)?.[0]?.title}
                    // console.log(equip.opStatus)
                    // console.log(equipmentOpStatusClassName)

                    return (
                  <div key={e} className={`small_motion_group${equipmentOpStatusClassName2}`}>
                    <p className="equipment_tit">* {equip.name}</p>
                    <div className="small_motion">
                      <div>
                        <div className={imgClassName} style={{textAlign:"center"}}>
                          <Link to={`/equipment/${equip.id}`}>
                          <div className="alert_box">
                            {alertEquipCnt > 0 ? <div className="orange_c"><span>!</span></div> :
                            (
                            (referEquipCnt > 0 ? <div className="yellow_c"><span>!</span></div> : '')
                            )
                            }
                          </div>
                          {/* {(lvl1.code === 'EV') && equip.latestEquipmentOp?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{equip.latestEquipmentOp?.currentFloor || '-'}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'IP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>} */}
                          {(lvl1.code === 'EV' || equip.equipmentCtgr?.code.startsWith('GEARED.') || equip.equipmentCtgr?.code.startsWith('GEARLESS.')) && equip.latestEquipmentOp?.currentFloor && <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{equip.latestEquipmentOp?.currentFloor || '-'}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'IP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>}
                          {/* {(lvl1.code === 'ES') && (equip.latestEquipmentOp?.opStatus === 'ACTIVE' ? <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'UP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>:<p className='floor' style={{color:"#fff"}}>■</p>)} */}
                          {(lvl1.code === 'ES' || equip.equipmentCtgr?.code.startsWith('DOUBLE.') || equip.equipmentCtgr?.code.startsWith('SINGLE.')) && (equip.latestEquipmentOp?.opStatus === 'ACTIVE' ? <p className='floor'><span className='font_green'>{equip.latestEquipmentOp?.opMode?.integratedName || ''}</span><span className={`${equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? 'downarrow':equip.latestEquipmentOp?.opStatusDetail === 'UP' ? 'uparrow':''} font_blue`}>{equip.latestEquipmentOp?.opStatusDetail === 'UP' ?'▲':equip.latestEquipmentOp?.opStatusDetail === 'DOWN' ? '▼':''}</span></p>:<p className='floor' style={{color:"#fff"}}>■</p>)}
                          <img src={`${process.env.PUBLIC_URL}/img/small_motion/${imgName}`} alt='' />
                          {/* {((lvl1.code === 'EV' )
                           || (lvl1.code === 'ES' )) &&  */}
                          {(((lvl1.code === 'EV' || equip.equipmentCtgr?.code.startsWith('GEARED.') || equip.equipmentCtgr?.code.startsWith('GEARLESS.'))
                           || (lvl1.code === 'ES' || equip.equipmentCtgr?.code.startsWith('DOUBLE.') || equip.equipmentCtgr?.code.startsWith('SINGLE.'))) 
                           && equip?.latestEquipmentOp?.controlPanelCode) &&
                            <p className={`error${equip?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? ' font_green': ''}`}>
                              {equip?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? t('message.common.normal') : equip?.latestEquipmentOp?.controlPanelCode?.usrDfnCd}
                            </p>
                          }
                          </Link>
                        </div>

                        <TableContainer className="table_part">
                          <Table aria-label="simple table">
                            <TableBody>
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.equipment.level3')}</TableCell>
                                <TableCell>{equip.equipmentCtgr.name}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.iso')}</TableCell>
                                <TableCell className={isoGrade?.color || ''}>{isoGrade?.title || '-'}</TableCell>
                              </TableRow>
                              {process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' &&
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.equipment.temperature')}</TableCell>
                                <TableCell className={temperatureByEquip?.highest && getTemperatureColor(temperatureByEquip?.highest.toFixed(0)) || ''}>{temperatureByEquip?.highest?.toFixed(0) || '-'}</TableCell>
                                {/* <TableCell className={temperatureByEquip?.lowest && getTemperatureColor(temperatureByEquip?.lowest.toFixed(0)) || ''}>{temperatureByEquip?.lowest?.toFixed(0) || '-'}</TableCell> */}
                              </TableRow>
                              }
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.defect')}</TableCell>
                                <TableCell><div className={getEquipmentStatusColorByStatus(equip.status).className}>{getEnumValueByEnumNameAndCode('EquipmentFaultStatus',equip.status)?.[0]?.title}</div></TableCell>
                              </TableRow>
                              {(isViewCommError || role === ROLE.SA.code) && 
                              <TableRow>
                                <TableCell>{t('message.common.subtitle.iot.status')}</TableCell>
                                <TableCell><div className={equipmentOpStatusClassName}>{equipmentOpStatusText}</div></TableCell>
                              </TableRow>
                              }
                            </TableBody>
                          </Table>
                        </TableContainer>      
                      </div>
                    </div>
                  </div>
                    )
                  })}
                </div>
              </div>
            </div>
            )
          })}
          </MainCard>
        </div>
        }
      </div> 
    )
  }
}

export default DashboardMoniteringSub;